import { useState } from 'react';
import {
  CAMPAIGN_MESSAGE_TEMPLATE_LIMITS,
} from '@workspace/types';
import {
  CAMPAIGN_TEMPLATE_VARIABLE_SOURCE_LABELS,
  COBRANZAS_TEMPLATE_VARIABLE_SOURCES,
} from '@workspace/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MessageTemplatePreview } from './MessageTemplatePreview';
import type { MessageTemplateDraft } from './MessageTemplateEditor.utils';

export function MessageTemplateEditor({
  value,
  onChange,
  kindLocked = false,
}: {
  value: MessageTemplateDraft;
  onChange: (next: MessageTemplateDraft) => void;
  kindLocked?: boolean;
}) {
  const maxBody =
    value.channelKind === 'SMS'
      ? CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.smsBodyMax
      : CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.emailBodyMax;
  return (
    <div className='grid gap-6 md:grid-cols-2'>
      <div className='space-y-4'>
        {kindLocked ? null : (
          <div className='space-y-2'>
            <Label htmlFor='tpl-kind'>Canal</Label>
            <select
              id='tpl-kind'
              className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              value={value.channelKind}
              onChange={event =>
                onChange({ ...value, channelKind: event.target.value === 'EMAIL' ? 'EMAIL' : 'SMS' })
              }
            >
              <option value='SMS'>SMS</option>
              <option value='EMAIL'>Email</option>
            </select>
          </div>
        )}
        <div className='space-y-2'>
          <Label htmlFor='tpl-name'>Nombre</Label>
          <Input
            id='tpl-name'
            value={value.name}
            maxLength={CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.nameMax}
            onChange={event => onChange({ ...value, name: event.target.value })}
          />
        </div>
        {value.channelKind === 'EMAIL' ? (
          <div className='space-y-2'>
            <Label htmlFor='tpl-subject'>Asunto</Label>
            <Input
              id='tpl-subject'
              value={value.subject}
              maxLength={CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.emailSubjectMax}
              onChange={event => onChange({ ...value, subject: event.target.value })}
            />
          </div>
        ) : null}
        <div className='space-y-2'>
          <Label htmlFor='tpl-body'>Cuerpo</Label>
          <Textarea
            id='tpl-body'
            value={value.bodyText}
            maxLength={maxBody}
            rows={value.channelKind === 'SMS' ? 6 : 10}
            onChange={event => onChange({ ...value, bodyText: event.target.value.slice(0, maxBody) })}
          />
        </div>
        {value.channelKind === 'EMAIL' ? (
          <div className='space-y-2'>
            <Label htmlFor='tpl-html'>HTML simple (opcional)</Label>
            <Textarea
              id='tpl-html'
              value={value.bodyHtml}
              maxLength={CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.emailBodyMax}
              rows={6}
              onChange={event =>
                onChange({
                  ...value,
                  bodyHtml: event.target.value.slice(0, CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.emailBodyMax),
                })
              }
            />
            <p className='text-xs text-muted-foreground'>Tipografia del sistema. No es un editor visual.</p>
          </div>
        ) : null}
        {value.channelKind === 'EMAIL' ? (
          <p className='text-xs text-amber-800 dark:text-amber-200'>
            Para campañas conversacionales por email, incluí <code>{'{{campaign.accessUrl}}'}</code> (obligatoria).
          </p>
        ) : null}
        <VariableHints channelKind={value.channelKind} />
      </div>
      <MessageTemplatePreview
        channelKind={value.channelKind}
        subject={value.subject}
        bodyText={value.bodyText}
      />
    </div>
  );
}

function VariableHints({ channelKind }: { channelKind: 'SMS' | 'EMAIL' }) {
  const [open, setOpen] = useState(false);
  return (
    <div className='space-y-2'>
      <button type='button' className='text-xs text-muted-foreground underline' onClick={() => setOpen(value => !value)}>
        {open ? 'Ocultar variables' : 'Variables que podes usar'}
      </button>
      {open ? (
        <ul className='grid gap-1 text-xs text-muted-foreground'>
          {COBRANZAS_TEMPLATE_VARIABLE_SOURCES.map(key => (
            <li key={key}>
              <code>{`{{${key}}}`}</code>
              {' · '}
              {CAMPAIGN_TEMPLATE_VARIABLE_SOURCE_LABELS[key]}
            </li>
          ))}
        </ul>
      ) : (
        <p className='text-xs text-muted-foreground'>
          Usa nombres, no numeros. Ejemplo: <code>{'{{contact.firstName}}'}</code>
          {channelKind === 'SMS' ? '.' : ' en asunto y cuerpo.'}
        </p>
      )}
    </div>
  );
}
